import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Button, Message, Modal, Select, TextArea } from "semantic-ui-react";
import { IAuthorization } from "../../models/authorization";
import { AppState } from "../../store/configureStore";
import { UserRole } from "../../actions/authentificationActions";
import {
    compareDates,
    constructDate,
    dateInit,
    deconstructDate,
    getTodayDate,
} from "../../models/common";
import { agent } from "../../api/agent";
import { toast } from "../..";
import { TranslateProps } from "../../i18n/TranslateProps";
import { DateInput } from "../../components/DateInput/DateInput";
import { CertificateStatus, certificateStatusOptions } from "../../models/certificate";

type Props = {
    open: boolean;
    close: () => void;
    authorization: IAuthorization;
    fetchCrewMember: () => Promise<void>;
};

export const ModifyAuthorizationForm = ({ open, close, authorization, fetchCrewMember }: Props) => {
    const user = useSelector((state: AppState) => state.user);
    const intl = useIntl();
    const [status, setStatus] = useState(
        user.role === UserRole.EnforcementAuthorityUser ||
            authorization.issuingAuthority !== user.authority
            ? CertificateStatus.Suspended
            : authorization.status
    );
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(
        !authorization.suspensionStartDate || authorization.suspensionStartDate.includes("0001-")
            ? getTodayDate()
            : deconstructDate(authorization.suspensionStartDate)
    );
    const [endDate, setEndDate] = useState(
        !authorization.suspensionEndDate || authorization.suspensionEndDate.includes("0001-")
            ? dateInit
            : deconstructDate(authorization.suspensionEndDate)
    );
    const [reason, setReason] = useState(authorization.reason ?? "");
    const [datesError, setDatesError] = useState(false);
    const [startDateError, setStartDateError] = useState(false);
    const [endDateError, setEndDateError] = useState(false);
    const options = certificateStatusOptions.filter(x => x.value !== CertificateStatus.Expired);

    const handleModifyAuthorization = async () => {
        setLoading(true);
        try {
            const dto: Partial<IAuthorization> = {
                status: status,
                suspensionStartDate: constructDate(startDate),
                suspensionEndDate: constructDate(endDate),
                reason: reason,
                qualificationID: authorization.qualificationID,
                type: authorization.type,
                issuingAuthority: authorization.issuingAuthority,
            };
            await agent.Authorizations.modify(dto);
            if (
                status === CertificateStatus.Suspended &&
                user.role !== UserRole.EnforcementAuthorityUser &&
                authorization.issuingAuthority !== user.authority
            ) {
                setLoading(false);
            } else {
                toast(intl.formatMessage({ id: "updatedAuthorization" }), true);
                await fetchCrewMember();
            }
        } catch {
            toast(intl.formatMessage({ id: "unableToUpdateAuthorization" }), false);
        } finally {
            setLoading(false);
            close();
        }
    };

    useEffect(() => {
        setEndDate(
            !authorization.suspensionEndDate || authorization.suspensionEndDate.includes("0001-")
                ? dateInit
                : deconstructDate(authorization.suspensionEndDate)
        );
        setReason(authorization.reason || "");
    }, [authorization.reason, authorization.suspensionEndDate, status]);

    useEffect(() => {
        //compare suspension dates
        if (!compareDates(startDate, endDate)) setDatesError(true);
        else setDatesError(false);
        //compare suspension start date with authorization start date
        if (!compareDates(deconstructDate(authorization.startDate), startDate, true))
            setStartDateError(true);
        else setStartDateError(false);
        //compare suspension end date with authorization end date
        if (!compareDates(endDate, deconstructDate(authorization.endDate), true))
            setEndDateError(true);
        else setEndDateError(false);
    }, [authorization.endDate, authorization.startDate, endDate, startDate]);

    return (
        <>
            <Modal
                open={open}
                onClose={() => close()}
                dimmer="blurring"
                size="small"
                className="modify-certificate-modal"
                closeOnDimmerClick={!loading}
            >
                <Modal.Header>{intl.formatMessage({ id: "modifyAuthorization" })}</Modal.Header>
                <Modal.Content>
                    <section className="input-container" id="topAddAuthorization">
                        <label>{intl.formatMessage({ id: "authorizationStatus" })}</label>
                        <Select
                            search
                            fluid
                            options={TranslateProps(intl, options)}
                            value={status}
                            onChange={(e, data: any) => setStatus(+data.value)}
                            disabled={user.role === UserRole.EnforcementAuthorityUser}
                        />
                    </section>
                    {status === CertificateStatus.Suspended ||
                    status === CertificateStatus.Withdrawn ? (
                        <>
                            <form className="add-certificate-form">
                                <section className="input-container">
                                    <label>
                                        {intl.formatMessage({ id: "startDate" })}
                                        <span className="input-required">*</span>
                                    </label>
                                    <DateInput
                                        date={startDate}
                                        setDate={setStartDate}
                                        extendedYears
                                    />
                                </section>
                                {status === CertificateStatus.Suspended && (
                                    <section className="input-container">
                                        <label>
                                            {intl.formatMessage({ id: "endDate" })}
                                            <span className="input-required">*</span>
                                        </label>
                                        <DateInput
                                            date={endDate}
                                            setDate={setEndDate}
                                            extendedYears
                                        />
                                    </section>
                                )}
                            </form>
                            <section id="bottomAddCertificate" className="input-container">
                                <label>
                                    {intl.formatMessage({ id: "comments" })}
                                    <span className="input-required">*</span>
                                </label>
                                <TextArea
                                    value={reason}
                                    onChange={e => setReason(e.target.value)}
                                    rows={5}
                                />
                            </section>
                            {status === CertificateStatus.Suspended && (
                                <>
                                    {datesError ? (
                                        <Message
                                            className="error-message"
                                            icon="exclamation circle"
                                            content={intl.formatMessage({
                                                id: "datesCompareError",
                                            })}
                                        />
                                    ) : startDateError ? (
                                        <Message
                                            className="error-message"
                                            icon="exclamation circle"
                                            content={intl.formatMessage({
                                                id: "suspensionStartDateError",
                                            })}
                                        />
                                    ) : endDateError ? (
                                        <Message
                                            className="error-message"
                                            icon="exclamation circle"
                                            content={intl.formatMessage({
                                                id: "suspensionEndDateError",
                                            })}
                                        />
                                    ) : null}
                                </>
                            )}
                        </>
                    ) : null}
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        primary
                        onClick={() => handleModifyAuthorization()}
                        content={intl.formatMessage({ id: "saveChanges" })}
                        fluid
                        loading={loading}
                        disabled={
                            loading ||
                            (status === CertificateStatus.Suspended &&
                                (!reason || datesError || startDateError || endDateError)) ||
                            (status === CertificateStatus.Withdrawn && !reason)
                        }
                    />
                </Modal.Actions>
            </Modal>
        </>
    );
};
